var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { getDataSourceName, } from '@sisense/sdk-data';
import { TranslatableError } from '../translation/translatable-error.js';
const API_DATASOURCES_BASE_PATH = 'api/datasources';
const API_DATAMODELS_BASE_PATH = 'api/v2/datamodels';
export class QueryApiDispatcher {
    constructor(httpClient) {
        this.httpClient = httpClient;
    }
    getDataSourceFields(dataSource, count = 9999, offset = 0) {
        const dataSourceName = getDataSourceName(dataSource);
        const url = `${API_DATASOURCES_BASE_PATH}/${encodeURI(dataSourceName)}/fields/search`;
        // when error is encountered, API returns only status code 400 without informative error message
        // to remedy, catch error and throw a more informative error message
        return this.httpClient.post(url, { offset, count }).catch(() => {
            throw new TranslatableError('errors.dataSourceNotFound', {
                dataSource: typeof dataSource === 'string' ? dataSource : dataSource.title,
            });
        });
    }
    /**
     * Returns a list of data sources.
     * This method works with user of viewer role or above.
     */
    getDataSourceList() {
        const url = `${API_DATASOURCES_BASE_PATH}/?sharedWith=r,w`;
        return this.httpClient.get(url);
    }
    /**
     * Returns the schema of a data source by its name.
     */
    getDataSourceSchema(dataSourceName) {
        const url = `${API_DATAMODELS_BASE_PATH}/schema?title=${encodeURIComponent(dataSourceName)}`;
        return this.httpClient.get(url);
    }
    sendJaqlRequest(dataSource, jaqlPayload) {
        const url = getJaqlUrl(dataSource);
        const abortController = new AbortController();
        return {
            responsePromise: this.httpClient
                .post(url, jaqlPayload, undefined, abortController.signal)
                .then((response) => {
                if (Array.isArray(response)) {
                    return {
                        values: [],
                        metadata: [],
                    };
                }
                return response;
            }),
            abortHttpRequest: (reason) => abortController.abort(reason),
        };
    }
    sendDownloadCsvRequest(dataSource, jaqlPayload) {
        const url = getDownloadCsvUrl(dataSource);
        const abortController = new AbortController();
        const params = new URLSearchParams();
        params.append('data', encodeURIComponent(JSON.stringify(jaqlPayload)));
        return {
            responsePromise: this.httpClient.post(url, params, { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }, abortController.signal, { nonJSONBody: true, returnBlob: true }),
            abortHttpRequest: (reason) => abortController.abort(reason),
        };
    }
    /**
     * Sends a request to cancel a JAQL query.
     *
     * @param guid - The guid of the query to cancel.
     * @param dataSource - The datasource of where query sent.
     */
    sendCancelJaqlQueryRequest(guid, dataSource) {
        return this.sendCancelMultipleJaqlQueriesRequest([guid], dataSource);
    }
    /**
     * Sends a request to cancel multiple JAQL queries.
     *
     * @param guids - The guids of the queries to cancel.
     * @param dataSource - The datasource of where queries sent.
     */
    sendCancelMultipleJaqlQueriesRequest(guids, dataSource) {
        return __awaiter(this, void 0, void 0, function* () {
            const payload = {
                queries: guids.join(';'),
            };
            const regularUrl = getRegularCancelQueryUrl(dataSource);
            try {
                yield this.httpClient.post(regularUrl, payload);
            }
            catch (error) {
                if (error.status === '404') {
                    // probably this datasource is live and requires a different URL for canceling queries
                    const liveUrl = getLiveCancelQueryUrl(dataSource);
                    yield this.httpClient.post(liveUrl, payload);
                }
                else {
                    throw error;
                }
            }
        });
    }
}
/**
 * Returns the URL for canceling queries for a regular (non-live) datasource.
 *
 * @param dataSource - The data source of the query.
 */
function getRegularCancelQueryUrl(dataSource) {
    const datasourcePath = encodeURIComponent(getDataSourceName(dataSource));
    return `${API_DATASOURCES_BASE_PATH}/localhost/${datasourcePath}/cancel_queries`;
}
/**
 * Returns the URL for canceling queries for a live datasource.
 * Live datasources require a different URL for canceling queries.
 *
 * @param dataSource - The data source of the query.
 */
function getLiveCancelQueryUrl(dataSource) {
    const datasourcePath = encodeURIComponent(getDataSourceName(dataSource));
    return `${API_DATASOURCES_BASE_PATH}/live/${datasourcePath}/cancel_queries`;
}
/**
 * Returns the URL for sending a JAQL request.
 *
 * @param dataSource - The data source of the query.
 */
function getJaqlUrl(dataSource) {
    const dataSourcePath = encodeURIComponent(getDataSourceName(dataSource));
    return `${API_DATASOURCES_BASE_PATH}/${dataSourcePath}/jaql`;
}
/**
 * Returns the URL for sending a Download CSV request.
 *
 * @param dataSource - The data source of the query.
 */
function getDownloadCsvUrl(dataSource) {
    return `${getJaqlUrl(dataSource)}/csv`;
}
//# sourceMappingURL=query-api-dispatcher.js.map